<script setup lang="ts">
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
</script>

<template>
    <div class="px-4 sm:px-6 lg:px-8 py-8">
        <div class="sm:flex sm:items-center sm:justify-between">
            <div class="sm:flex-auto">
                <PageBreadcrumb page-title="Analytika" />
                <p class="mt-2 text-sm text-gray-700 dark:text-gray-300">Prehľad generovaných dokladov a štatistík</p>
            </div>
        </div>

        <!-- Monthly Comparison Cards -->
        <div class="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-2">
            <div class="bg-white overflow-hidden shadow-xs rounded-lg dark:bg-white/3">
                <div class="p-5">
                    <div class="flex items-center">
                        <div class="shrink-0">
                            <svg class="size-6 text-green-400" fill="none" viewBox="0 0 24 24"
                                stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div class="ml-5 w-0 flex-1">
                            <dl>
                                <dt class="text-sm font-medium text-gray-500 truncate dark:text-gray-400">Počet dokladov za aktuálny
                                    mesiac</dt>
                                <dd class="text-3xl font-semibold text-gray-900 dark:text-white/90">{{ currentMonthDocuments }}</dd>
                                <dd class="text-xs text-gray-500 mt-1 dark:text-gray-400">{{ getCurrentMonthName() }}</dd>
                            </dl>
                        </div>
                    </div>
                </div>
            </div>

            <div class="bg-white overflow-hidden shadow-xs rounded-lg dark:bg-white/3">
                <div class="p-5">
                    <div class="flex items-center">
                        <div class="shrink-0">
                            <svg class="size-6 text-blue-400" fill="none" viewBox="0 0 24 24"
                                stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div class="ml-5 w-0 flex-1">
                            <dl>
                                <dt class="text-sm font-medium text-gray-500 truncate dark:text-gray-400">Počet dokladov za
                                    predchádzajúci mesiac</dt>
                                <dd class="text-3xl font-semibold text-gray-900 dark:text-white/90">{{ previousMonthDocuments }}</dd>
                                <dd class="text-xs text-gray-500 mt-1 dark:text-gray-400">{{ getPreviousMonthName() }}</dd>
                            </dl>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Summary Cards -->
        <div class="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <div class="bg-white overflow-hidden shadow-xs rounded-lg dark:bg-white/3">
                <div class="p-5">
                    <div class="flex items-center">
                        <div class="shrink-0">
                            <svg class="size-6 text-gray-400" fill="none" viewBox="0 0 24 24"
                                stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <div class="ml-5 w-0 flex-1">
                            <dl>
                                <dt class="text-sm font-medium text-gray-500 truncate dark:text-gray-400">Celkový počet dokladov</dt>
                                <dd class="text-3xl font-semibold text-gray-900 dark:text-white/90">{{ totalDocuments }}</dd>
                            </dl>
                        </div>
                    </div>
                </div>
            </div>

            <div class="bg-white overflow-hidden shadow-xs rounded-lg dark:bg-white/3">
                <div class="p-5">
                    <div class="flex items-center">
                        <div class="shrink-0">
                            <svg class="size-6 text-gray-400" fill="none" viewBox="0 0 24 24"
                                stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                        </div>
                        <div class="ml-5 w-0 flex-1">
                            <dl>
                                <dt class="text-sm font-medium text-gray-500 truncate dark:text-gray-400">Počet spoločností</dt>
                                <dd class="text-3xl font-semibold text-gray-900 dark:text-white/90">{{ totalCompanies }}</dd>
                            </dl>
                        </div>
                    </div>
                </div>
            </div>

            <div class="bg-white overflow-hidden shadow-xs rounded-lg dark:bg-white/3">
                <div class="p-5">
                    <div class="flex items-center">
                        <div class="shrink-0">
                            <svg class="size-6 text-gray-400" fill="none" viewBox="0 0 24 24"
                                stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                        </div>
                        <div class="ml-5 w-0 flex-1">
                            <dl>
                                <dt class="text-sm font-medium text-gray-500 truncate dark:text-gray-400">Priemerný počet dokladov na
                                    spoločnosť
                                </dt>
                                <dd class="text-3xl font-semibold text-gray-900 dark:text-white/90">{{ averageDocsPerCompany }}</dd>
                            </dl>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Documents Table -->
        <TableCard title="Doklady podľa spoločností">
            <template #controls>
                <div class="flex flex-wrap items-start gap-3 sm:flex-row sm:items-center">
                    <TableDateRange
                        :model-value="{ start: filters.start_date, end: filters.end_date }"
                        @update:model-value="dateRangeChanged"
                    />
                    <TableFilterDropdown
                        :options="[{ value: '', label: 'Všetky' }, ...documentTypes.map(t => ({ value: String(t.id), label: t.name + (t.description ? ' - ' + t.description : '') }))]"
                        label="Typ dokladu"
                        :model-value="filters.document_type"
                        @update:model-value="filters.document_type = $event"
                    />
                    <TableFilterDropdown
                        :options="[{ value: '', label: 'Všetky' }, ...documentSubtypes.map(s => ({ value: String(s.id), label: s.name + ' - ' + s.description }))]"
                        label="Podtyp dokladu"
                        :model-value="filters.document_subtype"
                        @update:model-value="filters.document_subtype = $event"
                    />
                    <div class="flex items-end gap-2 shrink-0">
                        <button @click="applyFilters" type="button"
                            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-xs text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                            Filtrovať
                        </button>
                        <button @click="clearFilters" type="button"
                            class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:border-gray-700 dark:text-gray-300 dark:bg-gray-800 dark:hover:bg-white/5">
                            Vymazať
                        </button>
                    </div>
                </div>
            </template>

            <div class="max-w-full overflow-x-auto min-w-0">
                <Table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
                    <TableHeader
                        class="border-y border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"
                    >
                        <TableRow>
                            <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                                Spoločnosť
                            </TableCell>
                            <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                                IČO
                            </TableCell>
                            <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                                Počet dokladov
                            </TableCell>
                            <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                                Posledný doklad
                            </TableCell>
                            <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                                Akcie
                            </TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody class="divide-y divide-gray-100 dark:divide-gray-800">
                        <TableRow v-for="company in documentsByCompany" :key="company.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150">
                            <TableCell class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white/90">
                                {{ company.company_name }}
                            </TableCell>
                            <TableCell class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {{ company.ico }}
                            </TableCell>
                            <TableCell class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {{ company.documents_count }}
                            </TableCell>
                            <TableCell class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {{ company.last_document_date ? formatDate(company.last_document_date) : 'N/A' }}
                            </TableCell>
                            <TableCell class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                <router-link :to="{ name: 'Detail spoločnosti', params: { id: company.id } }"
                                    class="text-indigo-600 hover:text-indigo-900">
                                    Detail spoločnosti
                                </router-link>
                            </TableCell>
                        </TableRow>
                        <TableEmptyState
                            v-if="documentsByCompany.length === 0"
                            title="Žiadne doklady neboli nájdené"
                            hint="Skúste upraviť filtre."
                            colspan="5"
                        />
                    </TableBody>
                </Table>
            </div>

            <template #footer>
                <div class="flex items-center justify-between">
                    <TablePagination
                        v-if="pagination.total > pagination.per_page"
                        :current-page="pagination.current_page"
                        :total-pages="pagination.last_page"
                        @page-change="changePage"
                    />
                </div>
            </template>
        </TableCard>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import AnalyticsDataService from '@/services/AnalyticsDataService'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import { Table, TableHeader, TableBody, TableRow, TableCell } from '@/components/ui/table'
import { TableCard, TableEmptyState, TableFilterDropdown, TableDateRange } from '@/components/common/table'

interface CompanyDocumentData {
    id: number
    company_name: string
    ico: string
    documents_count: number
    last_document_date: string | null
}

interface DocumentTypeData {
    type_id: number
    type_name: string
    count: number
}

interface DocumentType {
    id: number
    name: string
    description?: string
}

interface DocumentSubtype {
    id: number
    name: string
    description: string
}

export default defineComponent({
    name: 'AnalyticsView',
    components: {
        Table,
        TableHeader,
        TableBody,
        TableRow,
        TableCell,
        TableCard,
        TableEmptyState,
        TableFilterDropdown,
        TableDateRange,
    },
    data() {
        return {
            loading: true,
            totalDocuments: 0,
            totalCompanies: 0,
            averageDocsPerCompany: '0',
            currentMonthDocuments: 0,
            previousMonthDocuments: 0,
            filters: {
                start_date: '',
                end_date: '',
                document_type: '',
                document_subtype: ''
            },
            documentsByCompany: [] as CompanyDocumentData[],
            documentTypes: [] as DocumentType[],
            documentSubtypes: [] as DocumentSubtype[],
            pagination: {
                current_page: 1,
                last_page: 1,
                per_page: 10,
                total: 0,
                from: 0,
                to: 0
            },
            documentTypeData: {
                labels: [] as string[],
                datasets: [
                    {
                        label: 'Počet dokladov',
                        backgroundColor: '#3b82f6',
                        data: [] as number[]
                    }
                ]
            },
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            stepSize: 1
                        }
                    }
                }
            }
        }
    },
    mounted() {
        this.loadDocumentTypes()
        this.loadDocumentSubtypes()
        this.loadAnalytics()
    },
    methods: {
        dateRangeChanged(range: { start: string; end: string } | null) {
            this.filters.start_date = range?.start ?? ''
            this.filters.end_date = range?.end ?? ''
        },
        async loadDocumentTypes() {
            try {
                const response = await AnalyticsDataService.getDocumentTypes()
                if (response.data.success) {
                    this.documentTypes = response.data.data
                }
            } catch (error) {
                console.error('Error loading document types:', error)
            }
        },
        async loadDocumentSubtypes() {
            try {
                const response = await AnalyticsDataService.getDocumentSubtypes()
                if (response.data.success) {
                    this.documentSubtypes = response.data.data
                }
            } catch (error) {
                console.error('Error loading document subtypes:', error)
            }
        },
        async loadAnalytics() {
            this.loading = true
            try {
                // Load overall statistics
                const dateFilters = this.filters.start_date || this.filters.end_date || this.filters.document_type || this.filters.document_subtype ? this.filters : undefined
                const analyticsResponse = await AnalyticsDataService.getDocumentAnalytics(dateFilters)
                if (analyticsResponse.data.success) {
                    this.totalDocuments = analyticsResponse.data.data.total_documents || 0
                    this.totalCompanies = analyticsResponse.data.data.total_companies || 0
                    this.currentMonthDocuments = analyticsResponse.data.data.current_month_documents || 0
                    this.previousMonthDocuments = analyticsResponse.data.data.previous_month_documents || 0
                    this.averageDocsPerCompany =
                        this.totalCompanies > 0
                            ? String((this.totalDocuments / this.totalCompanies).toFixed(2))
                            : '0'
                }                // Load documents by company
                await this.loadDocumentsByCompany(1)

                // Load document type distribution
                const typeResponse = await AnalyticsDataService.getDocumentTypeDistribution(dateFilters)
                if (typeResponse.data.success && typeResponse.data.data.length > 0) {
                    this.documentTypeData.labels = typeResponse.data.data.map((item: DocumentTypeData) => item.type_name || 'Neznámy')
                    this.documentTypeData.datasets[0].data = typeResponse.data.data.map((item: DocumentTypeData) => item.count)
                }
            } catch (error) {
                console.error('Error loading analytics:', error)
                toast.error('Chyba pri načítavaní analytických údajov')
            } finally {
                this.loading = false
            }
        },
        async loadDocumentsByCompany(page: number) {
            try {
                const dateFilters = this.filters.start_date || this.filters.end_date || this.filters.document_type || this.filters.document_subtype ? this.filters : undefined
                const response = await AnalyticsDataService.getDocumentsByCompany(page, dateFilters)
                if (response.data.success) {
                    this.documentsByCompany = response.data.data.data || []
                    this.pagination = {
                        current_page: response.data.data.current_page || 1,
                        last_page: response.data.data.last_page || 1,
                        per_page: response.data.data.per_page || 10,
                        total: response.data.data.total || 0,
                        from: response.data.data.from || 0,
                        to: response.data.data.to || 0
                    }
                }
            } catch (error) {
                console.error('Error loading documents by company:', error)
                toast.error('Chyba pri načítavaní dokladov podľa spoločností')
            }
        },
        changePage(page: number) {
            if (page >= 1 && page <= this.pagination.last_page) {
                this.loadDocumentsByCompany(page)
            }
        },
        formatDate(dateString: string) {
            if (!dateString) return 'N/A'
            const date = new Date(dateString)
            return date.toLocaleDateString('sk-SK', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            })
        },
        applyFilters() {
            this.loadAnalytics()
        },
        clearFilters() {
            this.filters.start_date = ''
            this.filters.end_date = ''
            this.filters.document_type = ''
            this.filters.document_subtype = ''
            this.loadAnalytics()
        },
        getCurrentMonthName() {
            const months = ['Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún', 'Júl', 'August', 'September', 'Október', 'November', 'December']
            return months[new Date().getMonth()]
        },
        getPreviousMonthName() {
            const months = ['Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún', 'Júl', 'August', 'September', 'Október', 'November', 'December']
            const prevMonth = new Date().getMonth() - 1
            return months[prevMonth < 0 ? 11 : prevMonth]
        }
    }
})
</script>
